import useDeleteCartItem from "api/CartResource/useDeleteCartItem";
import useUpdateCartItem from "api/CartResource/useUpdateCartItem";
import usePrice from "hooks/usePrice";
import React from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiTrash2, FiBook, FiPackage, FiMinus, FiPlus } from "react-icons/fi";
import useAuthStore from "store/authStore";
import { formatCurrency } from "utils";
import { BookFormat } from "utils/server-constants/bookFormats";
import { Role } from "utils/server-constants/roles";

const CartItem = ({
	title,
	coverUrl,
	author,
	cartId,
	quantity,
	format,
	book,
	onQuantityChange,
}) => {
	const { mutateAsync: mutateDeleteCart, isLoading: isDeleteCartLoading } = useDeleteCartItem(
		cartId,
		{
			suppressSuccessToast: true,
		}
	);
	const { hasRoles } = useAuthStore();
	const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
	const deleteTimeoutRef = React.useRef(null);

	const [cartItemQuantity, setCartItemQuantity] = React.useState(Number(quantity || 1));
	const [displayQuantity, setDisplayQuantity] = React.useState(Number(quantity || 1));
	const [isUpdatingQuantity, setIsUpdatingQuantity] = React.useState(false);
	const updateTimeoutRef = React.useRef(null);
	const lastCommittedQuantityRef = React.useRef(Number(quantity || 1));

	const { mutateAsync } = useUpdateCartItem(cartId);

	React.useEffect(() => {
		const safeQty = Number(quantity || 1);
		setCartItemQuantity(safeQty);
		setDisplayQuantity(safeQty);
		lastCommittedQuantityRef.current = safeQty;
	}, [quantity]);

	React.useEffect(() => {
		return () => {
			if (deleteTimeoutRef.current) {
				clearTimeout(deleteTimeoutRef.current);
			}
			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}
		};
	}, []);

	const canPurchaseMultiple = hasRoles([Role.Institution, Role.Educator]);

	const price = usePrice({
		isFree: false,
		usdPrice: book?.price,
		zwlPrice: book?.zwl_price,
		sellable: true,
	});

	const physicalPrice = usePrice({
		isFree: false,
		usdPrice: book?.physical_usd_price,
		zwlPrice: book?.physical_zwl_price,
		sellable: true,
	});

	const priceToShow = format === BookFormat.Physical ? physicalPrice : price;
	const isPhysical = format === BookFormat.Physical;

	const formatBadgeClass = isPhysical
		? "bg-blue-50 text-blue-700 border border-blue-100"
		: "bg-akgreen/10 text-akgreener border border-akgreen/15";

	const FormatIcon = isPhysical ? FiPackage : FiBook;

	const commitQuantity = React.useCallback(
		async (nextQuantity) => {
			const safeQuantity = Math.max(1, Number(nextQuantity || 1));
			if (safeQuantity === lastCommittedQuantityRef.current) {
				return;
			}

			setIsUpdatingQuantity(true);
			const previousCommitted = lastCommittedQuantityRef.current;
			lastCommittedQuantityRef.current = safeQuantity;
			setCartItemQuantity(safeQuantity);
			onQuantityChange?.(cartId, safeQuantity);

			try {
				await mutateAsync({ quantity: safeQuantity });
			} catch (error) {
				lastCommittedQuantityRef.current = previousCommitted;
				setCartItemQuantity(previousCommitted);
				setDisplayQuantity(previousCommitted);
				onQuantityChange?.(cartId, previousCommitted);
			} finally {
				setIsUpdatingQuantity(false);
			}
		},
		[cartId, mutateAsync, onQuantityChange]
	);

	const scheduleCommitQuantity = React.useCallback(
		(nextQuantity) => {
			const safeQuantity = Math.max(1, Number(nextQuantity || 1));
			setDisplayQuantity(safeQuantity);
			onQuantityChange?.(cartId, safeQuantity);

			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}

			updateTimeoutRef.current = setTimeout(() => {
				commitQuantity(safeQuantity);
			}, 450);
		},
		[cartId, commitQuantity, onQuantityChange]
	);

	const handleDecrement = () => {
		if (isUpdatingQuantity || isDeleteCartLoading) {
			return;
		}
		scheduleCommitQuantity(Math.max(1, displayQuantity - 1));
	};

	const handleIncrement = () => {
		if (isUpdatingQuantity || isDeleteCartLoading) {
			return;
		}
		scheduleCommitQuantity(displayQuantity + 1);
	};

	const handleInputChange = (e) => {
		if (isUpdatingQuantity || isDeleteCartLoading) {
			return;
		}
		const nextQuantity = Math.max(1, parseInt(e.target.value, 10) || 1);
		scheduleCommitQuantity(nextQuantity);
	};

	const handleDeleteClick = () => {
		if (deleteTimeoutRef.current) {
			clearTimeout(deleteTimeoutRef.current);
		}
		setShowDeleteConfirm(true);
		deleteTimeoutRef.current = setTimeout(() => {
			setShowDeleteConfirm(false);
		}, 5000);
	};

	const handleCancelDelete = () => {
		if (deleteTimeoutRef.current) {
			clearTimeout(deleteTimeoutRef.current);
		}
		setShowDeleteConfirm(false);
	};

	const handleConfirmDelete = async () => {
		if (deleteTimeoutRef.current) {
			clearTimeout(deleteTimeoutRef.current);
		}
		await mutateDeleteCart();
		setShowDeleteConfirm(false);
	};

	return (
		<div className="flex flex-row items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl border border-gray-200 hover:border-akgreen/30 transition-all duration-200">
			<div className="flex-shrink-0 w-16 h-24 sm:w-20 sm:h-28 rounded-lg overflow-hidden shadow-sm bg-gray-100">
				{coverUrl ? (
					<img src={coverUrl} alt={`${title} Cover`} className="w-full h-full object-cover" />
				) : (
					<div className="w-full h-full flex items-center justify-center bg-gray-200">
						<FiBook className="w-8 h-8 text-gray-400" />
					</div>
				)}
			</div>

			<div className="flex-1 min-w-0 space-y-2">
				<div>
					<h3 className="text-base font-semibold text-gray-900 line-clamp-2" title={title}>
						{title}
					</h3>
					<p className="text-sm text-gray-500 mt-0.5" title={author}>
						by {author}
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-2.5">
					<span
						className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${formatBadgeClass}`}
					>
						<FormatIcon className="w-3.5 h-3.5" />
						{format}
					</span>
					<span className="text-sm text-gray-500">{formatCurrency(priceToShow)} each</span>
				</div>

				<div className="flex flex-wrap items-center gap-3 pt-1">
					{canPurchaseMultiple ? (
						<div className="flex items-center gap-2">
							<span className="text-xs text-gray-500">Qty:</span>
							<div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50">
								<button
									type="button"
									onClick={handleDecrement}
									disabled={displayQuantity <= 1 || isUpdatingQuantity || isDeleteCartLoading}
									className="h-8 w-8 flex items-center justify-center text-gray-600 disabled:text-gray-300"
								>
									<FiMinus className="w-3.5 h-3.5" />
								</button>
								<input
									type="number"
									value={displayQuantity}
									onChange={handleInputChange}
									min="1"
									disabled={isDeleteCartLoading}
									className="w-11 h-8 text-center bg-transparent text-gray-900 text-sm font-medium focus:outline-none disabled:text-gray-400"
								/>
								<button
									type="button"
									onClick={handleIncrement}
									disabled={isUpdatingQuantity || isDeleteCartLoading}
									className="h-8 w-8 flex items-center justify-center text-gray-600 disabled:text-gray-300"
								>
									<FiPlus className="w-3.5 h-3.5" />
								</button>
							</div>
							{isUpdatingQuantity && (
								<AiOutlineLoading3Quarters className="w-3.5 h-3.5 animate-spin text-gray-400" />
							)}
						</div>
					) : (
						<div className="flex items-center gap-2">
							<span className="text-xs text-gray-500">Qty:</span>
							<span className="h-8 w-10 bg-gray-100 text-gray-900 text-sm font-medium rounded-lg flex items-center justify-center border border-gray-200">
								{cartItemQuantity}
							</span>
						</div>
					)}

					<div className="flex items-center gap-2 ml-auto">
						<span className="text-xs text-gray-500 hidden sm:inline">Total:</span>
						<span className="text-lg font-bold text-gray-900">
							{formatCurrency(priceToShow * displayQuantity)}
						</span>
					</div>
				</div>
			</div>

			<div className="flex-shrink-0 pt-0.5">
				{showDeleteConfirm ? (
					<div className="flex flex-col items-end gap-1 bg-red-50 rounded-lg p-2">
						<span className="text-xs text-gray-600">Remove this item?</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={handleCancelDelete}
								disabled={isDeleteCartLoading}
								className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded disabled:opacity-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleConfirmDelete}
								disabled={isDeleteCartLoading}
								className="text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded font-medium inline-flex items-center gap-1 disabled:opacity-60"
							>
								{isDeleteCartLoading ? (
									<AiOutlineLoading3Quarters className="w-3 h-3 animate-spin" />
								) : (
									<>
										<FiTrash2 className="w-3 h-3" />
										Remove
									</>
								)}
							</button>
						</div>
					</div>
				) : (
					<button
						type="button"
						onClick={handleDeleteClick}
						disabled={isDeleteCartLoading}
						className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-gray-400 hover:text-red-500 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
						title="Remove item"
					>
						<FiTrash2 className="w-4 h-4" />
						<span className="hidden sm:inline">Remove</span>
					</button>
				)}
			</div>
		</div>
	);
};

export default CartItem;
